/**
 * Finds the closest element with a data-item-id attribute from the event target.
 * Uses the native closest() method for optimal performance (~10x faster than composedPath() traversal).
 *
 * This function is optimized for o2d-frontend-packages's architecture where Shadow DOM is not used
 * (O2dAreaElement.createRenderRoot returns `this`). The closest() method provides
 * C++-level performance for DOM traversal.
 *
 * The function searches from the event target up through the DOM hierarchy and returns
 * the first data-item-id found. If multiple elements in the hierarchy have the attribute,
 * the closest one (to the event target) is returned.
 *
 * @param event - The mouse/pointer event to analyze
 * @returns The item ID if an item element was the target, null otherwise
 *
 * @remarks
 * **Future Consideration:** This function is currently used in click handlers. Consider replacing
 * with AreaCellPositionCache-based lookup in future refactoring for consistency with the hover
 * event system (PRD-020 Cell-Tracking Event System).
 *
 * @example
 * ```typescript
 * function handleClick(event: MouseEvent) {
 *   const itemId = findTargetItemId(event);
 *   if (itemId) {
 *     console.log('Target item:', itemId);
 *   } else {
 *     console.log('Clicked on background');
 *   }
 * }
 * ```
 */
export function findTargetItemId(event: Event): string | null {
    // Ensure event target is an HTMLElement (required for closest())
    if (!(event.target instanceof HTMLElement)) {
        return null;
    }

    // Use native closest() for optimal performance
    // Searches from event.target up the DOM hierarchy for [data-item-id]
    const itemElement = event.target.closest('[data-item-id]');

    return itemElement?.getAttribute('data-item-id') ?? null;
}
